import { FC, memo, useEffect, useState } from 'react';
import { Input, Button, Space, message } from 'antd';
import { useAtom, useSetAtom } from 'jotai';
import styled from 'styled-components';
import { UI_Header, UI_HeaderLeft, UI_HeaderRight, UI_HeaderRight_item } from '../PanelStyle';
import { FaXmark } from 'react-icons/fa6';
import { PiPencilSimpleBold } from 'react-icons/pi';
import { Rnd } from 'react-rnd';
import { UploadTask_Base } from '~/common/typescript/models/UploadTask';
import { UploadTask_Update_Request } from '~/common/typescript/apis/UploadTaskAPIs';
import { UploadTaskAPIs } from '~/common/typescript/apis/UploadTaskAPIs';
import { editingUploadTaskAtom, show_UploadTask_UpdatePanel_Atom, showPanel, hidePanel } from './store';
import { uploadTasksAtom } from '~/stores/UploadTaskStore';

const UploadTask_UpdatePanel: FC = () => {
  const [isOpen, setIsOpen] = useAtom(show_UploadTask_UpdatePanel_Atom);
  const [uploadTask, setUploadTask] = useAtom(editingUploadTaskAtom);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const setUploadTasks = useSetAtom(uploadTasksAtom);

  // No need for event listeners anymore as we're using Recoil state directly

  useEffect(() => {
    if (uploadTask) {
      setTitle(uploadTask.title || '');
    } else {
      setTitle('');
    }
  }, [uploadTask]);

  const handleClose = () => {
    hidePanel(setIsOpen);
    setUploadTask(null);
  };

  const handleShow = () => {
    showPanel(setIsOpen);
  };

  const performUpdate = async () => {
    if (!uploadTask?.id) return;

    setLoading(true);
    try {
      const payload: UploadTask_Update_Request = {
        title: title,
      };
      await UploadTaskAPIs.remote_updateUploadTask(uploadTask.id, payload);
      message.success('Cập nhật thành công!');

      // Update the task in the list
      setUploadTasks((prevTasks: UploadTask_Base[]) =>
        prevTasks.map((task: UploadTask_Base) =>
          task.id === uploadTask.id
            ? { ...task, title: title }
            : task
        )
      );

      handleClose();
    } catch (error) {
      console.error('Update failed', error);
      message.error('Cập nhật thất bại!');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Rnd
      default={{
        x: window.innerWidth / 2 - 300,
        y: window.innerHeight / 2 - 150,
        width: 600,
        height: 300,
      }}
      dragHandleClassName="draggable-header"
      minWidth={400}
      minHeight={200}
      style={{ zIndex: 1000 }}
      bounds="window"
    >
      <UI_PanelWrapper>
        <UI_Header className="draggable-header" style={{ cursor: 'move' }}>
          <UI_HeaderLeft style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <PiPencilSimpleBold style={{ color: '#2563eb', fontSize: '18px' }} />
            <span>{`Cập nhật tải lên: ${uploadTask?.title || ''}`}</span>
          </UI_HeaderLeft>
          <UI_HeaderRight>
            <UI_HeaderRight_item onClick={handleClose}>
              <FaXmark />
            </UI_HeaderRight_item>
          </UI_HeaderRight>
        </UI_Header>
        <UI_Content>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <FormItemWrapper label="Tiêu đề">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Nhập tiêu đề"
              />
            </FormItemWrapper>
          </Space>
        </UI_Content>
        <UI_Footer>
          <Space>
            <Button key="cancel" onClick={handleClose}>
              Hủy
            </Button>
            <Button
              key="save"
              type="primary"
              onClick={performUpdate}
              loading={loading}
            >
              Lưu
            </Button>
          </Space>
        </UI_Footer>
      </UI_PanelWrapper>
    </Rnd>
  );
};

export default memo(UploadTask_UpdatePanel);

const UI_PanelWrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  box-shadow: var(--shadows-paneShadow);
  z-index: 999999;
  display: flex;
  flex-direction: column;
  border: 1px solid #ccc;
`;

const UI_Content = styled.div`
  padding: 16px;
  flex-grow: 1;
  overflow-y: auto;
`;

const UI_Footer = styled.div`
  padding: 10px 16px;
  text-align: right;
  border-top: 1px solid #f0f0f0;
`;

const FormItemWrapper = styled.div<{ label: string; labelWidth?: number }>`
  margin-bottom: 12px;

  .ant-form-item-label {
    width: ${(props) => props.labelWidth || 120}px;
    text-align: left;
  }

  .ant-form-item-control {
    flex: 1;
  }
`;